import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Table,
  Typography,
  Divider,
  Spin,
} from "antd";

import { useSelector } from "react-redux";

import { manufacturingRecordWithDetailseApi } from "../../../../apiservice/manufacturing";
import { getInstitutionApi } from "../../../../apiservice/setting";

import {
  numberFormat,
  convertNumberToWords,
  formatToCompanyTimezone,
} from "../../../../lib/functions";

import "./voucher-mobile.css";

import { selectAuthInfo } from "../../../Auth/authSelectors";

const { Title, Text } = Typography;

const ManufacturingVoucherBody = ({
  mf_id,
  printMode = false,
  showStamp = false,
  showSignature = false,
  setVoucherNo,
  setAccName,
  institution: institutionProp,
  setCustomerEmail,
}) => {
  const [data, setData] = useState(null);
  const [localInstitution, setLocalInstitution] = useState(null);

  const authInfo = useSelector(selectAuthInfo);
  const currency =
    authInfo?.currency ||
    institutionProp?.currency ||
    localInstitution?.currency;

  const institution =
    institutionProp || localInstitution;

  useEffect(() => {
    if (!mf_id) return;

    manufacturingRecordWithDetailseApi({
      mf_id,
      from: "voucher",
    }).then((res) => {
      if (res?.data?.length) {
        const record = res.data[0];

        setData(record);

        if (!printMode && setVoucherNo) {
          setVoucherNo(record.mf_voucher_no);
          setAccName?.(record.acc_name);
        }

        if (record.email_address) {
          setCustomerEmail?.(record.email_address);
        }
      }
    });
  }, [mf_id, printMode, setVoucherNo, setAccName, setCustomerEmail]);

  useEffect(() => {
    if (institutionProp) return;

    getInstitutionApi()
      .then((res) => {
        if (res?.data) {
          setLocalInstitution(res.data);
        }
      })
      .catch((err) => {
        console.error(
          "Failed to load institution for manufacturing voucher",
          err
        );
      });
  }, [institutionProp]);

  if (!data) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin />
      </div>
    );
  }

  const finishedItems = data.miDetails || [];
  const rawItems = data.mciDetails || [];

  const columnsFinished = [
    {
      title: "NO.",
      render: (_, __, index) => index + 1,
      width: 40,
    },
    {
      title: "FINISHED ITEM",
      dataIndex: "item_name",
    },
    {
      title: "QTY",
      dataIndex: "pd_qty",
      align: "right",
      width: 80,
    },
    {
      title: "RATE",
      dataIndex: "item_rate",
      align: "right",
      width: 90,
      render: (value) => numberFormat(value),
    },
    {
      title: "PER",
      dataIndex: "unit_name",
      align: "right",
      width: 80,
    },
    {
      title: "TOTAL",
      dataIndex: "item_total",
      align: "right",
      width: 100,
      render: (value) => numberFormat(value),
    },
  ];

  const columnsRaw = [
    {
      title: "NO.",
      render: (_, __, index) => index + 1,
      width: 40,
    },
    {
      title: "CONSUMED ITEM",
      dataIndex: "item_name",
    },
    {
      title: "QTY",
      dataIndex: "raw_qty",
      align: "right",
      width: 80,
    },
    {
      title: "RATE",
      dataIndex: "raw_item_rate",
      align: "right",
      width: 90,
      render: (value) => numberFormat(value),
    },
    {
      title: "PER",
      dataIndex: "unit_name",
      align: "right",
      width: 80,
    },
    {
      title: "TOTAL",
      dataIndex: "raw_item_total",
      align: "right",
      width: 100,
      render: (value) => numberFormat(value),
    },
  ];

  const isPharma =
    institution?.country?.trim().toLowerCase() ===
      "united arab emirates" &&
    (
      institution?.subscription_type
        ?.toLowerCase()
        .includes("pharma") ||
      institution?.subscription_type
        ?.toLowerCase()
        .includes("pharmabooks")
    );

  return (
    <div
      data-testid="manufacturing-voucher-card"
      className="invoice-v2 voucher-section"
    >
      {/* =========================
          HEADER
      ========================= */}

      <Row
        justify="space-between"
        align="top"
        className="invoice-header"
      >
        <Col
          flex="0 0 320px"
          className="bill-to"
          style={{ marginTop: "58px" }}
        >
          <Text strong>
            Voucher No: &nbsp;{data.mf_voucher_no}
          </Text>

          <br />

          <Text>
            Date: &nbsp;
            {formatToCompanyTimezone(data.created_date)
              ?.split(" ")[0]
              ?.split("-")
              ?.reverse()
              ?.join("-")}
          </Text>

          {data.acc_name && (
            <>
              <br />

              <Text strong>
                Account: &nbsp;{data.acc_name}
              </Text>
            </>
          )}

          {data.customer_po_ref && (
            <>
              <br />

              <Text>
                Customer PO Ref: &nbsp;
                {data.customer_po_ref}
              </Text>
            </>
          )}

          {data.payment_terms && (
            <>
              <br />

              <Text>
                Payment Terms: &nbsp;
                {data.payment_terms} Days
              </Text>
            </>
          )}
        </Col>

        <Col flex="320px" className="invoice-title">
          <Title level={2}>MANUFACTURING</Title>

          <Text strong>
            Voucher By: {data.full_name}
          </Text>

          <br />

          <Text className="wrap-text">
            Address: {institution?.street_address || "-"}
          </Text>          

          {institution?.phone_no && (
            <>
              <Text>
                Contact No: &nbsp;
                {institution.phone_no}
              </Text>

              <br />
            </>
          )}

          {data.acc_mgr && (
            <>
              <Text>
                {isPharma
                  ? "Rep.: "
                  : "Account Manager: "}
                {data.acc_mgr}
              </Text>

              <br />
            </>
          )}

          {institution?.vat_license && (
            <>
              <Text>
                {institution?.country
                  ?.trim()
                  .toLowerCase() ===
                "united arab emirates"
                  ? "TRN No: "
                  : "Tax Reg No: "}

                {institution.vat_license}
              </Text>

              <br />
            </>
          )}

          {data.licence_no && (
            <Text>
              License No: {data.licence_no}
            </Text>
          )}
        </Col>
      </Row>

      {/* =========================
          PRODUCED ITEMS
      ========================= */}

      <Title
        level={5}
        style={{
          marginTop: 16,
          marginBottom: 8,
        }}
      >
        Produced Items
      </Title>

      <Table
        className="invoice-items"
        dataSource={finishedItems}
        columns={columnsFinished}
        pagination={false}
        size="small"
        rowKey={(record, index) =>
          record.id1 || record.item_id || index
        }
        style={{ marginBottom: 24 }}
      />

      {/* =========================
          CONSUMED ITEMS
      ========================= */}

      <Title
        level={5}
        style={{
          marginTop: 16,
          marginBottom: 8,
        }}
      >
        Consumed Items
      </Title>

      <Table
        className="invoice-items"
        dataSource={rawItems}
        columns={columnsRaw}
        pagination={false}
        size="small"
        rowKey={(record, index) =>
          record.id2 || record.item_id || index
        }
      />

      {/* =========================
          TOTAL SECTION
      ========================= */}

      <Divider />

      <Row>
        <Col span={16} />

        <Col span={8} style={{ textAlign: "right" }}>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Text>Labour Cost :</Text>

              <Text>
                {numberFormat(data.labour_cost || 0)}
              </Text>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Text>Other Cost :</Text>

              <Text>
                {numberFormat(data.others_cost || 0)}
              </Text>
            </div>

            <div
              style={{
                borderTop: "1px solid #000",
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 4,
                marginTop: 4,
              }}
            >
              <Text strong>
                Grand Total ({currency || ""}):
              </Text>

              <Text strong>
                {numberFormat(data.total_cost || 0)}
              </Text>
            </div>
          </div>
        </Col>
      </Row>

      {/* =========================
          FOOTER
      ========================= */}

      <Divider />

      <Row>
        <Col span={24}>
          <Text>
            In Words :{" "}
            {convertNumberToWords(
              data.total_cost || 0,
              currency
            )}
          </Text>

          <br />

          <Text>
            Description : {data.narration || "-"}
          </Text>
        </Col>
      </Row>

      {(showStamp || showSignature) && (
        <div className="invoice-footer-section">
          <div className="footer-right-group">
            {showStamp && institution?.stamp && (
              <div className="stamp-block">
                <img src={institution.stamp} alt="Stamp" />
              </div>
            )}

            {showSignature && institution?.signature && (
              <div className="signature-block">
                <img src={institution.signature} alt="Signature" />
                <div className="sign-line-text">Authorized Signature</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManufacturingVoucherBody;